import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  Users, 
  DollarSign, 
  FileText, 
  Package, 
  AlertTriangle, 
  Clock, 
  PieChart, 
  PackagePlus, 
  ArrowDown, 
  Zap,
  Menu,
  X,
  Shield
} from 'lucide-react';
import { BiAnalyse } from 'react-icons/bi';
import { FaHamburger } from 'react-icons/fa';

function PlutoMenu({ rank, user_id }) {
  const { referral_id, page, second_page, third_page } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Load the collapsed state from localStorage if available
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsCollapsed(JSON.parse(savedState));
    }
  }, []);
  
  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [page, second_page, third_page]);
  
  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Helper function to determine if a menu item is active
  const isActive = (menuPage) => {
    return page === 'pluto' && second_page === menuPage;
  };
  
  // Menu items with access control based on rank
  const menuItems = [
    { 
      label: 'Dashboard', 
      path: '/pluto', 
      icon: <Home size={18} />, 
      access: true, 
      isActive: second_page === undefined,
      category: 'main'
    },
    { 
      label: 'Users', 
      path: '/pluto/user', 
      icon: <Users size={18} />, 
      access: rank !== 4 && rank !== 7,
      isActive: isActive('user'),
      category: 'management'
    },
    { 
      label: 'Withdrawals', 
      path: '/pluto/withdrawals', 
      icon: <DollarSign size={18} />, 
      access: rank === 10,
      isActive: isActive('withdrawals'),
      category: 'financial'
    },
    { 
      label: 'Invoices', 
      path: '/pluto/invoices', 
      icon: <FileText size={18} />, 
      access: rank === 10,
      isActive: isActive('invoices'),
      category: 'financial'
    },
    { 
      label: 'PTCS', 
      path: '/pluto/ptcs', 
      icon: <Package size={18} />, 
      access: rank === 10,
      isActive: isActive('ptcs'),
      category: 'financial'
    },
    { 
      label: 'Chargebacks', 
      path: '/pluto/chargebacks', 
      icon: <AlertTriangle size={18} />, 
      access: rank >= 8,
      isActive: isActive('chargebacks'),
      category: 'financial'
    },
    { 
      label: 'Pending Offers', 
      path: '/pluto/pending-offers', 
      icon: <Clock size={18} />, 
      access: rank >= 8,
      isActive: isActive('pending-offers'),
      category: 'offers'
    },
     { 
      label: 'Suggested Pending Offers', 
      path: '/pluto/suggested-pending-offers', 
      icon: <AlertTriangle size={18} />, 
      access: rank >= 8,
      isActive: isActive('suggested-pending-offers'),
      category: 'offers'
    },
    { 
      label: 'Suspect Offers', 
      path: '/pluto/suspect-offers', 
      icon: <AlertTriangle size={18} />, 
      access: rank >= 8,
      isActive: isActive('suspect-offers'),
      category: 'offers'
    },
    { 
      label: 'OfferWalls', 
      path: '/pluto/offerwalls', 
      icon: <Package size={18} />, 
      access: rank === 10,
      isActive: isActive('offerwalls'),
      category: 'offers'
    },
    { 
      label: 'PaidCash Offers', 
      path: '/pluto/pc-offers', 
      icon: <DollarSign size={18} />, 
      access: rank === 10,
      isActive: isActive('pc-offers'),
      category: 'offers'
    },
    { 
      label: 'Offers', 
      path: '/pluto/offers', 
      icon: <Package size={18} />, 
      access: rank >= 8,
      isActive: isActive('offers'),
      category: 'offers'
    },
    { 
      label: 'Offer Analytics', 
      path: '/pluto/offer-analytics', 
      icon: <BiAnalyse size={18} />, 
      access: rank >= 8,
      isActive: isActive('offer-analytics'),
      category: 'analytics'
    },
    { 
      label: 'Offers Data', 
      path: '/pluto/offer-total', 
      icon: <PieChart size={18} />, 
      access: rank >= 8,
      isActive: isActive('offer-total'),
      category: 'analytics'
    },
    { 
      label: 'Offers Data Today', 
      path: '/pluto/offer-today', 
      icon: <PieChart size={18} />, 
      access: rank >= 8,
      isActive: isActive('offer-today'),
      category: 'analytics'
    },
    { 
      label: 'New Offers', 
      path: '/pluto/offers-today', 
      icon: <PackagePlus size={18} />, 
      access: rank >= 8,
      isActive: isActive('offers-today'),
      category: 'analytics'
    },
    { 
      label: 'Withdrawals Today', 
      path: '/pluto/withdrawals-today', 
      icon: <ArrowDown size={18} />, 
      access: rank >= 8,
      isActive: isActive('withdrawals-today'),
      category: 'analytics'
    },
    { 
      label: 'Fast Completion', 
      path: '/pluto/offer-fastc', 
      icon: <Zap size={18} />, 
      access: rank >= 8,
      isActive: isActive('offer-fastc'),
      category: 'analytics'
    }
  ];

  const accessibleMenuItems = menuItems.filter(item => item.access);
  const activeItem = accessibleMenuItems.find(item => item.isActive);

  // Group menu items by category for better organization
  const groupedMenuItems = accessibleMenuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  const CategoryHeader = ({ title, isCollapsed }) => (
    !isCollapsed && (
      <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
        {title}
      </div>
    )
  );

  const MenuItem = ({ item, isMobile = false, onClick }) => (
    <Link 
      to={item.path} 
      className={`group relative flex items-center transition-all duration-200 ${
        isMobile 
          ? 'px-4 py-3 text-gray-300 hover:bg-gray-800 hover:text-white' 
          : `px-3 py-2.5 mx-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white ${isCollapsed ? 'justify-center' : ''}`
      } ${
        item.isActive 
          ? isMobile 
            ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-500' 
            : 'bg-blue-600/20 text-blue-400 shadow-lg shadow-blue-500/20'
          : ''
      }`}
      onClick={onClick}
      title={isCollapsed && !isMobile ? item.label : ''}
    >
      <span className={`${isCollapsed && !isMobile ? '' : 'mr-3'} flex-shrink-0 ${item.isActive ? 'text-blue-400' : ''}`}>
        {item.icon}
      </span>
      
      {(!isCollapsed || isMobile) && (
        <span className="text-sm font-medium truncate">{item.label}</span>
      )}
      
      {/* Tooltip for collapsed desktop state */}
      {isCollapsed && !isMobile && (
        <div className="absolute left-full ml-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
          {item.label}
          <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-1 w-2 h-2 bg-gray-800 rotate-45"></div>
        </div>
      )}
    </Link>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="fixed top-0 left-0 right-0 z-50 bg-gray-900 border-b border-gray-700 shadow-lg">
          <div className="flex items-center justify-between px-4 py-3">
             <div onClick={toggleMobileMenu} class="sidebar-toggle">
                <FaHamburger/>
            </div>
            
            
            {/* Current Page Indicator */}
            {activeItem && (
              <div className="flex items-center space-x-2 bg-gray-800 px-3 py-1.5 rounded-lg">
                <span className="text-blue-400">{activeItem.icon}</span>
                <span className="text-sm text-gray-300 font-medium">
                  {activeItem.label}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile content spacer */}
        <div className="h-16"></div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 z-40 bg-black/60"
              onClick={toggleMobileMenu}
            ></div>
            <nav className="fixed top-0 left-0 bottom-0 z-50 flex flex-col w-72 max-w-[85vw] bg-gray-900 shadow-2xl border-r border-gray-700">
              {/* Mobile Menu Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-700">
               
               <div onClick={toggleMobileMenu} class="sidebar-toggle">
                <FaHamburger/>
            </div>
              </div>
              
              {/* Mobile Menu Items */}
              <div className="flex-1 overflow-y-auto py-2">
                {Object.entries(groupedMenuItems).map(([category, items]) => (
                  <div key={category} className="mb-1">
                    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {category}
                    </div>
                    {items.map((item, index) => (
                      <MenuItem 
                        key={index} 
                        item={item} 
                        isMobile={true} 
                        onClick={toggleMobileMenu}
                      />
                    ))}
                  </div>
                ))}
              </div>
              
              {/* Mobile Menu Footer */}
              <div className="px-4 py-3 border-t border-gray-700">
                <div className="text-xs text-gray-500">
                  Rank: {rank} • User: {user_id}
                </div>
              </div>
            </nav>
          </>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div 
          className={`fixed top-0 left-0 h-full bg-gray-900 border-r border-gray-700 shadow-xl transition-all duration-300 ease-in-out z-30 ${
            isCollapsed ? 'w-16' : 'w-64'
          }`}
        >
          {/* Desktop Header */}
          <div onClick={toggleSidebar} class="sidebar-toggle">
            {isCollapsed ?<svg  xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right" aria-hidden="true"><path d="m9 18 6-6-6-6"></path></svg> :            
             <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-left" aria-hidden="true"><path d="m15 18-6-6 6-6"></path></svg>
}
            </div>
          
          
          {/* Desktop Menu Items */}
          <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600">
            {Object.entries(groupedMenuItems).map(([category, items]) => (
              <div key={category} className="mb-2">
                <CategoryHeader title={category} isCollapsed={isCollapsed} />
                {items.map((item, index) => (
                  <MenuItem key={index} item={item} />
                ))}
                {!isCollapsed && <div className="h-2"></div>}
              </div>
            ))}
          </nav>
          
          {/* Desktop Footer */}
          {!isCollapsed && (
            <div className="px-4 py-3 border-t border-gray-700">
              <div className="text-xs text-gray-500">
                Rank: <span className="text-blue-400 font-medium">{rank}</span> • 
                User: <span className="text-blue-400 font-medium">{user_id}</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Main Content Spacer */}
        <div className={`transition-all duration-300 ease-in-out ${isCollapsed ? 'ml-16' : 'ml-64'}`}></div>
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .scrollbar-thin::-webkit-scrollbar {
          width: 4px;
        }
        .scrollbar-track-gray-800::-webkit-scrollbar-track {
          background-color: rgb(31, 41, 55);
        }
        .scrollbar-thumb-gray-600::-webkit-scrollbar-thumb {
          background-color: rgb(75, 85, 99);
          border-radius: 2px;
        }
        .scrollbar-thumb-gray-600::-webkit-scrollbar-thumb:hover {
          background-color: rgb(107, 114, 128);
        }
      `}</style>
    </>
  );
}

export default PlutoMenu;